import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Zap, PhoneOff, Copy, CheckCircle2, Apple } from 'lucide-react';
import { Button } from '../ui/Button';
import { useTheme } from '../../contexts/ThemeContext';
type ForwardTarget = 'vexu' | 'other' | 'disabled';
interface ForwardingScenario {
  id: string;
  name: string;
  icon: React.ElementType;
  status: ForwardTarget;
  otherNumber?: string;
}
interface ActivationOverlaysProps {
  syncPhase: 'idle' | 'confirm' | 'scanning' | 'synced';
  scanProgress: number;
  scanText: string;
  confirmSync: () => void;
  setSyncPhase: (phase: 'idle' | 'confirm' | 'scanning' | 'synced') => void;
  switchPhase: 'confirm' | 'activating' | 'done' | null;
  switchTarget: ForwardingScenario | null;
  confirmSwitch: () => void;
  setSwitchPhase: (phase: 'confirm' | 'activating' | 'done' | null) => void;
  setSwitchTarget: (target: ForwardingScenario | null) => void;
  disablePhase: 'confirm' | 'disabling' | 'done' | null;
  disableTarget: ForwardingScenario | null;
  confirmDisable: () => void;
  setDisablePhase: (phase: 'confirm' | 'disabling' | 'done' | null) => void;
  setDisableTarget: (target: ForwardingScenario | null) => void;
  VEXU_NUMBER: string;
  platform?: 'ios' | 'android';
}
export function ActivationOverlays({
  syncPhase,
  scanProgress,
  scanText,
  confirmSync,
  setSyncPhase,
  switchPhase,
  switchTarget,
  confirmSwitch,
  setSwitchPhase,
  setSwitchTarget,
  disablePhase,
  disableTarget,
  confirmDisable,
  setDisablePhase,
  setDisableTarget,
  VEXU_NUMBER,
  platform = 'android'
}: ActivationOverlaysProps) {
  const {
    isLight
  } = useTheme();
  const UssdConfirmModal = ({
    title,
    description,
    onConfirm,
    onCancel
  }: {
    title: string;
    description: string;
    onConfirm: () => void;
    onCancel: () => void;
  }) => <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-50">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} data-id="element-51" />
      <motion.div initial={{
      y: 200,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 200,
      opacity: 0
    }} transition={{
      type: 'spring',
      stiffness: 400,
      damping: 30
    }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-52">
        <div className="p-6" data-id="element-53">
          <h3 className={`text-lg font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-54">
            {title}
          </h3>
          <p className={`text-sm mb-6 leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-55">
            {description}
          </p>
          <div className="space-y-3" data-id="element-56">
            <Button onClick={onConfirm} fullWidth data-id="element-57">
              Continue
            </Button>
            <button onClick={onCancel} className={`w-full py-3 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-58">
              Cancel
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>;
  const IosManualModal = ({
    title,
    ussdCode,
    phoneNumber,
    onConfirm,
    onCancel
  }: {
    title: string;
    ussdCode: string;
    phoneNumber: string;
    onConfirm: () => void;
    onCancel: () => void;
  }) => {
    const [copiedUssd, setCopiedUssd] = useState(false);
    const [copiedPhone, setCopiedPhone] = useState(false);
    const copyToClipboard = (text: string, isUssd: boolean) => {
      navigator.clipboard.writeText(text);
      if (isUssd) {
        setCopiedUssd(true);
        setTimeout(() => setCopiedUssd(false), 2000);
      } else {
        setCopiedPhone(true);
        setTimeout(() => setCopiedPhone(false), 2000);
      }
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-59">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} data-id="element-60" />
        <motion.div initial={{
        y: 200,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 200,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30
      }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-61">
          <div className="p-6" data-id="element-62">
            <div className="flex items-center gap-2 mb-2" data-id="element-63">
              <Apple className={`w-5 h-5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-64" />
              <h3 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-65">
                {title}
              </h3>
            </div>

            <p className={`text-sm mb-5 leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-66">
              On iOS, you need to manually dial the USSD code
              {phoneNumber ? ' or update forwarding in your iPhone Settings.' : '.'}
            </p>

            <div className="space-y-4 mb-6" data-id="element-67">
              {/* USSD Code Box */}
              <div data-id="element-68">
                <label className={`text-xs font-bold uppercase tracking-wider mb-1.5 block ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-69">
                  {phoneNumber ? 'Option 1: Dial USSD Code' : 'Dial USSD Code'}
                </label>
                <div className={`flex items-center justify-between p-3 rounded-xl border ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10'}`} data-id="element-70">
                  <code className={`text-sm font-mono font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-71">
                    {ussdCode}
                  </code>
                  <button onClick={() => copyToClipboard(ussdCode, true)} className={`p-2 rounded-lg transition-colors ${copiedUssd ? 'bg-vexu/10 text-vexu' : isLight ? 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50' : 'bg-white/10 text-zinc-400 hover:bg-white/20'}`} data-id="element-72">
                    {copiedUssd ? <CheckCircle2 className="w-4 h-4" data-id="element-73" /> : <Copy className="w-4 h-4" data-id="element-74" />}
                  </button>
                </div>
              </div>

              {/* Phone Number Box (Only for enabling) */}
              {phoneNumber && <div data-id="element-75">
                  <label className={`text-xs font-bold uppercase tracking-wider mb-1.5 block ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-76">
                    Option 2: iOS Settings
                  </label>
                  <p className={`text-[11px] mb-2 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-77">
                    Settings &gt; Phone &gt; Call Forwarding
                  </p>
                  <div className={`flex items-center justify-between p-3 rounded-xl border ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10'}`} data-id="element-78">
                    <span className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-79">
                      {phoneNumber}
                    </span>
                    <button onClick={() => copyToClipboard(phoneNumber, false)} className={`p-2 rounded-lg transition-colors ${copiedPhone ? 'bg-vexu/10 text-vexu' : isLight ? 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50' : 'bg-white/10 text-zinc-400 hover:bg-white/20'}`} data-id="element-80">
                      {copiedPhone ? <CheckCircle2 className="w-4 h-4" data-id="element-81" /> : <Copy className="w-4 h-4" data-id="element-82" />}
                    </button>
                  </div>
                </div>}
            </div>

            <div className="space-y-3" data-id="element-83">
              <Button onClick={onConfirm} fullWidth data-id="element-84">
                {phoneNumber ? "I've enabled it" : "I've disabled it"}
              </Button>
              <button onClick={onCancel} className={`w-full py-3 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-85">
                Cancel
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>;
  };
  const ScanningOverlay = () => <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.4,
    ease: 'easeInOut'
  }} className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8" data-id="element-86">
      <div className={`absolute inset-0 ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-87" />
      <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-88">
        <div className="relative w-32 h-32 mb-8" data-id="element-89">
          <motion.div animate={{
          scale: [1, 1.25, 1],
          opacity: [0.25, 0.08, 0.25]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-full border-2 border-vexu/30" data-id="element-90" />
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-vexu border-r-vexu/30" data-id="element-91" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-92">
            <motion.div animate={{
            scale: [1, 1.08, 1]
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-93">
              <RefreshCw className="w-10 h-10 text-vexu" data-id="element-94" />
            </motion.div>
          </div>
        </div>
        <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-95">
          Checking Carrier Settings
        </p>
        <div className="h-5 relative w-full flex justify-center mb-6" data-id="element-96">
          <AnimatePresence mode="wait" data-id="element-97">
            <motion.p key={scanText} initial={{
            opacity: 0,
            y: 8,
            filter: 'blur(4px)'
          }} animate={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} exit={{
            opacity: 0,
            y: -8,
            filter: 'blur(4px)'
          }} transition={{
            duration: 0.6,
            ease: 'easeInOut'
          }} className={`text-sm absolute ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-98">
              {scanText}
            </motion.p>
          </AnimatePresence>
        </div>
        <div className="w-full max-w-[200px]" data-id="element-99">
          <div className={`h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-100">
            <motion.div className="h-full rounded-full bg-vexu" initial={{
            width: '0%'
          }} animate={{
            width: `${scanProgress}%`
          }} transition={{
            duration: 0.8,
            ease: [0.25, 0.46, 0.45, 0.94]
          }} data-id="element-101" />
          </div>
        </div>
      </div>
    </motion.div>;
  const ActivationOverlay = () => <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8" data-id="element-102">
      <div className={`absolute inset-0 ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-103" />
      <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-104">
        <div className="relative w-32 h-32 mb-8" data-id="element-105">
          <motion.div animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.1, 0.3]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-full border-2 border-vexu/30" data-id="element-106" />
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-vexu border-r-vexu/30" data-id="element-107" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-108">
            <motion.div animate={{
            scale: [1, 1.1, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} data-id="element-109">
              <Zap className="w-10 h-10 text-vexu" data-id="element-110" />
            </motion.div>
          </div>
        </div>
        <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-111">
          Activating Vexu.ai
        </p>
        <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-112">
          Configuring forwarding for "{switchTarget?.name}"...
        </p>
      </div>
    </motion.div>;
  const DisableOverlay = () => <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8" data-id="element-113">
      <div className={`absolute inset-0 ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-114" />
      <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-115">
        <div className="relative w-32 h-32 mb-8" data-id="element-116">
          <motion.div animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.1, 0.3]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-full border-2 border-red-500/30" data-id="element-117" />
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-red-500 border-r-red-500/30" data-id="element-118" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-119">
            <motion.div animate={{
            scale: [1, 1.1, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} data-id="element-120">
              <PhoneOff className="w-10 h-10 text-red-500" data-id="element-121" />
            </motion.div>
          </div>
        </div>
        <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-122">
          Disabling Vexu.ai
        </p>
        <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-123">
          Removing forwarding for "{disableTarget?.name}"...
        </p>
      </div>
    </motion.div>;
  return <>
      <AnimatePresence data-id="element-124">
        {syncPhase === 'confirm' && <UssdConfirmModal title="Carrier Action Required" description="Vexu.ai needs to send a carrier USSD command to check your call forwarding settings for this line. This helps us verify where your calls are forwarded." onConfirm={confirmSync} onCancel={() => setSyncPhase('idle')} data-id="element-125" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-126">
        {syncPhase === 'scanning' && <ScanningOverlay data-id="element-127" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-128">
        {switchPhase === 'confirm' && switchTarget && (platform === 'ios' ? <IosManualModal title={`Forward: ${switchTarget.name}`} ussdCode={`**67*${VEXU_NUMBER.replace(/[^0-9+]/g, '')}#`} phoneNumber={VEXU_NUMBER} onConfirm={confirmSwitch} onCancel={() => {
        setSwitchPhase(null);
        setSwitchTarget(null);
      }} data-id="element-129" /> : <UssdConfirmModal title={`Forward: ${switchTarget.name}`} description={`Vexu.ai will dial a USSD code (e.g. **67*${VEXU_NUMBER.replace(/[^0-9+]/g, '')}#) to forward calls "${switchTarget.name}" to the Vexu.ai number (${VEXU_NUMBER}). Once activated, calls ${switchTarget.name.toLowerCase()} will be answered by your AI assistant.`} onConfirm={confirmSwitch} onCancel={() => {
        setSwitchPhase(null);
        setSwitchTarget(null);
      }} data-id="element-130" />)}
      </AnimatePresence>

      <AnimatePresence data-id="element-131">
        {(switchPhase === 'activating' || switchPhase === 'done') && <ActivationOverlay data-id="element-132" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-133">
        {disablePhase === 'confirm' && disableTarget && (platform === 'ios' ? <IosManualModal title={`Disable: ${disableTarget.name}`} ussdCode={`##67#`} phoneNumber="" onConfirm={confirmDisable} onCancel={() => {
        setDisablePhase(null);
        setDisableTarget(null);
      }} data-id="element-134" /> : <UssdConfirmModal title="Disable Forwarding" description={`Vexu.ai will send a USSD command to remove forwarding for "${disableTarget.name}". Missed calls under this condition will no longer be handled by your AI assistant.`} onConfirm={confirmDisable} onCancel={() => {
        setDisablePhase(null);
        setDisableTarget(null);
      }} data-id="element-135" />)}
      </AnimatePresence>

      <AnimatePresence data-id="element-136">
        {(disablePhase === 'disabling' || disablePhase === 'done') && <DisableOverlay data-id="element-137" />}
      </AnimatePresence>
    </>;
}